<script setup lang="ts">
import { Gesuch } from '@/models/Gesuch'
import LiegenschaftFormFields from '@/components/liegenschaft/form-fields.vue'
import FieldInfoIcon from '@/components/field-info-icon.vue'
import { Liegenschaft } from '@/models/Liegenschaft'
import dayjs, { Dayjs } from 'dayjs'
import { computed, onMounted, ref, type Ref } from 'vue'
import type { ObjektRahmen } from '@/models/apollon_conf_fk/ObjektRahmen'
import { LiegenschaftData } from '@/models/apollon_data/LiegenschaftData'
import { createModel } from '@kp/js-model'
import { useToast } from '@/composables/toast'
import { errToStr } from '@/helpers/tools'
import { formRules } from '@/helpers/validators'
import { ProjektFeld } from '@/models/apollon_conf_fk/ProjektFeld'
import ProjektFeldField from '@/components/projekt-feld-field.vue'
import { useI18n } from 'vue-i18n'
import { Data } from '@/models/apollon_data/Data'

const {
    gesuch,
    liegenschaft = null,
    readOnly = true,
} = defineProps<{
    gesuch: Gesuch
    liegenschaft?: LiegenschaftData | null
    readOnly?: boolean
}>()

const { t } = useI18n()
const loading = ref(false)
const initialLoading = ref(true)
const liegenschaftEntry: Ref<Liegenschaft | null> = ref(null)
const zusatzdatenEntry: Ref<{
    [key: string]: { value: number | string | null; display: string | null }
}> = ref({})
const heizsystemFilter: Ref<{ property: string; value: string | number | null }[] | null> =
    ref(null)
const valid = ref(false)

const emit = defineEmits<{
    (e: 'close'): void
}>()

const rules = formRules

const zusatzdatenConfig = computed(() => {
    return gesuch.config?.config?.objekte || null
})

onMounted(() => {
    loadLiegenschaftData()
})
function loadLiegenschaftData() {
    loading.value = true
    initialLoading.value = true
    zusatzdatenEntry.value = { ...liegenschaft?.liegenschaft_zusatzdaten }
    liegenschaftEntry.value = liegenschaft?.liegenschaft
        ? createModel(Liegenschaft, liegenschaft.liegenschaft)
        : null
    initialLoading.value = false
    loading.value = false
}
function closeForm() {
    emit('close')
}
async function onSubmit() {
    loading.value = true
    if (!liegenschaftEntry.value?.id) {
        try {
            await liegenschaftEntry.value?.save()
            const mod = createModel(LiegenschaftData, {
                liegenschaft: liegenschaftEntry.value,
                liegenschaft_zusatzdaten: zusatzdatenEntry.value,
            })
            const data = createModel(Data, gesuch.data || undefined)
            const ls = data?.get('liegenschaften') || []
            ls.push(mod)
            gesuch?.set('data', data)
        } catch (e) {
            useToast().add({ text: errToStr(e), color: 'error' })
        }
    } else {
        const liegenschaft = gesuch?.data?.liegenschaften.find(
            (e) => e.liegenschaft?.id === liegenschaftEntry.value?.id,
        )
        if (liegenschaft) {
            liegenschaft.liegenschaft = liegenschaftEntry.value
            liegenschaft.liegenschaft_zusatzdaten = zusatzdatenEntry.value
        }
    }
    loading.value = false
    closeForm()
}
function isComponentVisible(comp: ObjektRahmen | ProjektFeld) {
    let visible = true
    if (gesuch.phase === 'einreichung') {
        if (comp.einreichung_from) {
            visible = visible && dayjs(comp.einreichung_from).isBefore(dayjs())
        }
        if (comp.einreichung_to) {
            visible = visible && dayjs().isBefore(dayjs(comp.einreichung_to).add(1, 'day'))
        }
        return visible && comp.einreichung
    }
    if (gesuch.phase === 'abschluss') {
        if (comp.abschluss_from) {
            visible = visible && dayjs(comp.abschluss_from).isBefore(dayjs())
        }
        if (comp.abschluss_to) {
            visible = visible && dayjs().isBefore(dayjs(comp.abschluss_to).add(1, 'day'))
        }
        return visible && comp.abschluss
    }
    return false
}
function onHeizungstypFieldChange() {
    const value = zusatzdatenEntry.value.heizungstyp_id
    heizsystemFilter.value = [{ property: 'heizungstyp_id', value: value ? value.value : null }]
}

const updateFeldFieldValue = (
    value: string | number | object | Dayjs | null | undefined,
    feld: ProjektFeld,
) => {
    if (feld?.feld_name) {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            const obj = value as { id: number | string; value: string }
            zusatzdatenEntry.value[feld.feld_name] = {
                value: obj.id,
                display: obj.value,
            }
        } else {
            zusatzdatenEntry.value[feld.feld_name] = {
                value: (value ?? null) as number | string | null,
                display: value ? String(value) : '',
            }
        }
        if (feld.feld_name === 'heizungstyp_id') {
            onHeizungstypFieldChange()
        }
    }
}
</script>

<template>
    <v-card>
        <v-skeleton-loader
            v-if="initialLoading"
            class="centered-card"
            type="list-item-avatar, list-item-three-line@3, actions"
        ></v-skeleton-loader>
        <div v-else>
            <v-toolbar color="primary white--text" class="colored-toolbar">
                <v-toolbar-title>
                    <v-icon left color="white">fas fa-building</v-icon>
                    {{ t('liegenschaft') }}
                </v-toolbar-title>
            </v-toolbar>
            <v-container v-if="zusatzdatenConfig" fluid>
                <v-card class="centered-card">
                    <v-form v-model="valid" autocomplete="new-password">
                        <v-card-title class="ml-4 mt-4">
                            {{ zusatzdatenConfig.rahmen['objekt_felder'].label }}
                            <field-info-icon
                                :infotext="zusatzdatenConfig.rahmen['objekt_felder'].hilfetext"
                                left
                            />
                        </v-card-title>
                        <v-card-text>
                            <LiegenschaftFormFields
                                :liegenschaft="liegenschaftEntry"
                                :rules="rules"
                                :loading="loading"
                                :read-only="readOnly"
                                :felder-config="zusatzdatenConfig.rahmen.objekt_felder || {}"
                                dense
                            ></LiegenschaftFormFields>
                        </v-card-text>
                    </v-form>
                </v-card>
                <v-form
                    v-if="zusatzdatenEntry && zusatzdatenConfig.visible"
                    no-gutters
                    autocomplete="new-password"
                >
                    <template v-for="(rahmen, key) in zusatzdatenConfig.rahmen">
                        <v-card
                            v-if="isComponentVisible(rahmen) && key !== 'objekt_felder'"
                            :key="key"
                            class="centered-card"
                        >
                            <v-card-title class="ml-4 mt-4">
                                {{ rahmen.label }}
                                <field-info-icon :infotext="rahmen.hilfetext" left />
                            </v-card-title>
                            <v-card-text>
                                <v-container class="mx-0">
                                    <!--
                                            filter: The field 'Energietraeger' gets the selected Heizsystem-id as filter,
                                            which is captured via input event
                                        -->
                                    <template
                                        v-for="feld in rahmen.felder"
                                        :key="feld.feld_name || 'new'"
                                    >
                                        <v-row v-if="feld.feld_name && isComponentVisible(feld)">
                                            <v-col cols="12" sm="6">
                                                <ProjektFeldField
                                                    v-if="isComponentVisible(feld)"
                                                    :model-value="
                                                        zusatzdatenEntry[feld.feld_name]?.display
                                                    "
                                                    modelValueAsObject
                                                    :projektfeld="feld"
                                                    :phase="gesuch.phase"
                                                    :read-only="readOnly"
                                                    :hide-on-empty="
                                                        feld.feld_name === 'energietraeger_id'
                                                    "
                                                    :filter="
                                                        feld.feld_name === 'energietraeger_id'
                                                            ? heizsystemFilter
                                                            : null
                                                    "
                                                    @update:model-value="
                                                        (value) =>
                                                            updateFeldFieldValue(value, feld)
                                                    "
                                                ></ProjektFeldField>
                                            </v-col>
                                        </v-row>
                                    </template>
                                </v-container>
                            </v-card-text>
                        </v-card>
                    </template>
                </v-form>
            </v-container>
        </div>
        <v-card-actions>
            <v-spacer />
            <v-btn
                class="hidden-sm-and-up"
                color="decline"
                text
                :disabled="loading"
                @click="closeForm"
                append-icon="fas fa-arrow-left"
            >
            </v-btn>
            <v-btn class="hidden-xs" color="decline" :disabled="loading" text @click="closeForm">
                {{ t('close') }}
            </v-btn>
            <v-btn
                v-if="!readOnly"
                class="hidden-xs"
                variant="flat"
                color="action white--text"
                type="submit"
                :disabled="loading || !valid"
                :loading="loading"
                @click="onSubmit"
                prepend-icon="fa-solid fa-floppy-disk"
            >
                {{ t('save') }}
            </v-btn>
            <v-btn
                v-if="!readOnly"
                class="hidden-sm-and-up"
                variant="flat"
                color="action white--text"
                type="submit"
                icon="fa-solid fa-floppy-disk"
                :disabled="loading || !valid"
                :loading="loading"
                @click="onSubmit"
            >
            </v-btn>
        </v-card-actions>
    </v-card>
</template>
